import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, MapPin, Check } from 'lucide-react';
import { geocoderApi, ResolvedAddress } from '../services/geocoderApi';
import type { DeliveryAddress } from '../services/ordersApi';

declare global {
  interface Window {
    ymaps?: any;
    _ymapsLoading?: Promise<void>;
  }
}

const MAPS_API_KEY = process.env.REACT_APP_YANDEX_MAPS_KEY ?? '';

const DEFAULT_MAP_LAT = 55.9336;
const DEFAULT_MAP_LNG = 37.5155;

function loadYandexMaps(): Promise<void> {
  if (window.ymaps) return Promise.resolve();
  if (window._ymapsLoading) return window._ymapsLoading;
  window._ymapsLoading = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://api-maps.yandex.ru/2.1/?apikey=${MAPS_API_KEY}&lang=ru_RU&coordorder=longlat`;
    script.async = true;
    script.onload = () => {
      window.ymaps.ready(() => resolve());
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return window._ymapsLoading;
}

interface Props {
  open: boolean;
  initialLat?: number;
  initialLng?: number;
  onClose: () => void;
  onConfirm: (address: DeliveryAddress) => void;
}

export function AddressMapPicker({ open, initialLat, initialLng, onClose, onConfirm }: Props) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(false);
  const [resolved, setResolved] = useState<ResolvedAddress | null>(null);
  const [noKey, setNoKey] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const reverseGeocode = useCallback(async (lat: number, lng: number) => {
    setResolving(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const result = await geocoderApi.resolveByCoords(lat, lng);
      setResolved(result);
      setResolving(false);
    }, 600);
  }, []);

  useEffect(() => {
    if (!open) return;
    if (!MAPS_API_KEY) { setNoKey(true); setLoading(false); return; }

    const lat =
      initialLat != null && initialLat !== 0 && !Number.isNaN(initialLat) ? initialLat : DEFAULT_MAP_LAT;
    const lng =
      initialLng != null && initialLng !== 0 && !Number.isNaN(initialLng) ? initialLng : DEFAULT_MAP_LNG;
    const center: [number, number] = [lng, lat];

    setLoading(true);
    setResolved(null);

    loadYandexMaps()
      .then(() => {
        if (!mapRef.current) {
          setLoading(false);
          return;
        }
        const ymaps = window.ymaps;

        const map = new ymaps.Map(mapRef.current, {
          center,
          zoom: 15,
          controls: ['zoomControl'],
        });

        const placemark = new ymaps.Placemark(
          center,
          {},
          {
            draggable: true,
            preset: 'islands#blackStretchyIcon',
          },
        );

        placemark.events.add('dragend', () => {
          const coords: [number, number] = placemark.geometry.getCoordinates();
          reverseGeocode(coords[1], coords[0]);
        });

        map.events.add('click', (e: any) => {
          const coords: [number, number] = e.get('coords');
          placemark.geometry.setCoordinates(coords);
          reverseGeocode(coords[1], coords[0]);
        });

        map.geoObjects.add(placemark);
        mapInstanceRef.current = map;
        markerRef.current = placemark;
        setLoading(false);

        reverseGeocode(center[1], center[0]);
      })
      .catch(() => {
        setLoading(false);
      });

    return () => {
      mapInstanceRef.current?.destroy();
      mapInstanceRef.current = null;
      markerRef.current = null;
    };
  }, [open, initialLat, initialLng, reverseGeocode]);

  const handleConfirm = useCallback(() => {
    if (!resolved) return;
    onConfirm({
      city: resolved.city,
      street: resolved.street,
      house: resolved.house,
      yandexAddressId: resolved.yandexAddressId,
      formattedAddress: resolved.formattedAddress,
    });
    onClose();
  }, [resolved, onConfirm, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] flex flex-col"
          style={{ background: '#000' }}
        >
          {/* Map */}
          <div className="flex-1 relative min-h-0" style={{ minHeight: '40vh' }}>
            <div ref={mapRef} className="absolute inset-0" style={{ width: '100%', height: '100%' }} />

            {loading && (
              <div className="absolute inset-0 flex items-center justify-center" style={{ background: '#1a1a1a' }}>
                <Loader2 size={32} className="animate-spin" style={{ color: '#fff' }} />
              </div>
            )}

            {noKey && (
              <div className="absolute inset-0 flex flex-col items-center justify-center px-8 text-center" style={{ background: '#1a1a1a' }}>
                <MapPin size={40} style={{ color: '#666' }} className="mb-3" />
                <p className="text-white font-semibold mb-1">Карта недоступна</p>
                <p className="text-sm" style={{ color: '#888' }}>
                  Добавьте <code>REACT_APP_YANDEX_MAPS_KEY</code> в .env
                </p>
              </div>
            )}

            {/* Close button */}
            <button
              type="button"
              onClick={onClose}
              className="absolute top-4 right-4 w-10 h-10 rounded-2xl flex items-center justify-center z-10 transition-all active:scale-90"
              style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)', pointerEvents: 'auto' }}
            >
              <X size={20} style={{ color: '#fff' }} />
            </button>

            {/* Hint */}
            <div
              className="absolute top-4 left-1/2 -translate-x-1/2 rounded-2xl px-4 py-2 text-xs font-medium z-10 pointer-events-none"
              style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)', color: '#fff', whiteSpace: 'nowrap' }}
            >
              Перетащите метку или нажмите на карту
            </div>
          </div>

          {/* Bottom sheet */}
          <div
            className="shrink-0 px-4 pt-4 pb-6"
            style={{ background: '#f4f4f4', borderTopLeftRadius: 24, borderTopRightRadius: 24 }}
          >
            <div className="flex items-start gap-3 mb-4">
              <MapPin size={18} className="shrink-0 mt-0.5" style={{ color: '#0a0a0a' }} />
              <div className="flex-1 min-w-0">
                {resolving ? (
                  <div className="flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin" style={{ color: '#aaa' }} />
                    <span className="text-sm" style={{ color: '#aaa' }}>Определяем адрес...</span>
                  </div>
                ) : resolved ? (
                  <>
                    <p className="text-sm font-semibold" style={{ color: '#0a0a0a' }}>
                      {resolved.formattedAddress || `${resolved.city}, ${resolved.street}, ${resolved.house}`}
                    </p>
                    {!resolved.house && (
                      <p className="text-xs mt-0.5" style={{ color: '#e53e3e' }}>
                        Уточните точный адрес с номером дома
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-sm" style={{ color: '#aaa' }}>Выберите точку на карте</p>
                )}
              </div>
            </div>

            <button
              onClick={handleConfirm}
              disabled={!resolved || !resolved.house || resolving}
              className="w-full py-4 rounded-2xl font-bold text-base text-white flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-50"
              style={{ background: '#0a0a0a', boxShadow: '0 6px 20px rgba(0,0,0,0.2)' }}
            >
              <Check size={18} />
              Подтвердить адрес
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
